/**
 * SafetyMoreMenu — dropdown for the profile "⋯" More button.
 *
 * Actions: Mention, Copy Link, Mute, Restrict, Block, Report.
 * Reads state from `/api/safety/state/{handle}` so labels reflect
 * the current relationship (Mute → Unmute, Block → Unblock).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MoreHorizontal, AtSign, Link as LinkIcon, VolumeX, EyeOff, Ban, Flag,
  Loader2, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { key: "harassment",    label: "Harassment or bullying" },
  { key: "hate",          label: "Hate or discrimination" },
  { key: "threats",       label: "Threats or violence" },
  { key: "sexual",        label: "Sexual content" },
  { key: "child_safety",  label: "Child safety concern" },
  { key: "spam",          label: "Spam" },
  { key: "scam",          label: "Scam or fraud" },
  { key: "impersonation", label: "Impersonation" },
  { key: "misinformation",label: "Misinformation" },
  { key: "privacy",       label: "Privacy violation" },
  { key: "self_harm",     label: "Self-harm concern" },
  { key: "other",         label: "Something else" },
];


export default function SafetyMoreMenu({ handle, userId, canonicalUrl, onChange }) {
  const [open, setOpen]         = useState(false);
  const [state, setState]       = useState({});
  const [busy, setBusy]         = useState(false);
  const [showReport, setShowReport] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/safety/state/${handle}`, { withCredentials: true })
      .then((r) => setState(r.data))
      .catch(() => setState({}));
  }, [handle, open]);

  const run = async (fn, label) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); toast.success(label); await onChange?.(); }
    catch { toast.error("Something went wrong"); }
    finally { setBusy(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(canonicalUrl || window.location.href);
    toast.success("Profile link copied");
  };

  const toggleBlock = () => run(async () => {
    if (state.is_blocked) await axios.delete(`${API}/block/${handle}`, { withCredentials: true });
    else                   await axios.post(`${API}/block/${handle}`, {}, { withCredentials: true });
    setState({ ...state, is_blocked: !state.is_blocked });
    setOpen(false);
  }, state.is_blocked ? "Unblocked" : `Blocked @${handle}`);

  const toggleMute = () => run(async () => {
    if (state.is_muted) await axios.delete(`${API}/mute/${handle}`, { withCredentials: true });
    else                 await axios.post(`${API}/mute/${handle}`, { scope: "posts" }, { withCredentials: true });
    setState({ ...state, is_muted: !state.is_muted });
    setOpen(false);
  }, state.is_muted ? "Unmuted" : "Muted");

  const toggleRestrict = () => run(async () => {
    if (state.is_restricted) await axios.delete(`${API}/restrict/${handle}`, { withCredentials: true });
    else                      await axios.post(`${API}/restrict/${handle}`, {}, { withCredentials: true });
    setState({ ...state, is_restricted: !state.is_restricted });
    setOpen(false);
  }, state.is_restricted ? "No longer restricted" : "Restricted");

  return (
    <>
      <div className="relative">
        <button
          data-testid="more-btn"
          onClick={() => setOpen((s) => !s)}
          className="px-3 py-2 rounded-full text-[13px] font-medium border bg-white"
          style={{ borderColor: "#CBD5E1", color: "#0F172A" }}
        >
          <MoreHorizontal className="w-3.5 h-3.5" />
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
            <div
              data-testid="more-menu"
              className="absolute right-0 mt-2 w-[210px] rounded-lg bg-white border shadow-lg py-1 z-40"
              style={{ borderColor: "#E5E7EB" }}
            >
              <MenuItem icon={AtSign}   label={`Mention @${handle}`}
                        onClick={() => { navigator.clipboard.writeText(`@${handle}`); toast.success("Mention copied"); setOpen(false); }} />
              <MenuItem icon={LinkIcon} label="Copy profile link"
                        onClick={() => { copyLink(); setOpen(false); }} />
              <div className="my-1 h-px" style={{ background: "#F1F5F9" }} />
              <MenuItem icon={VolumeX}  label={state.is_muted ? "Unmute" : "Mute posts"}
                        onClick={toggleMute} testid="menu-mute" busy={busy} />
              <MenuItem icon={EyeOff}   label={state.is_restricted ? "Unrestrict" : "Restrict"}
                        onClick={toggleRestrict} testid="menu-restrict" busy={busy} />
              <MenuItem icon={Ban}      label={state.is_blocked ? "Unblock" : "Block"}
                        onClick={toggleBlock} testid="menu-block" busy={busy}
                        danger={!state.is_blocked} />
              <div className="my-1 h-px" style={{ background: "#F1F5F9" }} />
              <MenuItem icon={Flag}     label="Report profile"
                        onClick={() => { setShowReport(true); setOpen(false); }}
                        testid="menu-report" danger />
            </div>
          </>
        )}
      </div>

      {showReport && (
        <ReportModal
          handle={handle}
          userId={userId}
          onClose={() => setShowReport(false)}
        />
      )}
    </>
  );
}


function MenuItem({ icon: Icon, label, onClick, danger, testid, busy }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-2 text-[13px] hover:bg-slate-50 text-left"
      style={{ color: danger ? "#DC2626" : "#334155" }}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
}


function ReportModal({ handle, userId, onClose }) {
  const [reason, setReason]           = useState("");
  const [description, setDescription] = useState("");
  const [autoBlock, setAutoBlock]     = useState(false);
  const [busy, setBusy]               = useState(false);
  const [done, setDone]               = useState(null);

  const submit = async () => {
    if (!reason || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/safety/reports`, {
        target_type: "profile",
        target_id:   userId || handle,
        reason,
        description,
        auto_block:  autoBlock,
      }, { withCredentials: true });
      setDone(r.data.case_id);
    } catch (e) {
      toast.error(e?.response?.status === 429 ? "Too many reports today"
                                                : "Could not submit report");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(15,15,20,0.55)" }}>
      <div
        data-testid="report-modal"
        className="w-full max-w-md rounded-xl bg-white border p-5"
        style={{ borderColor: "#E5E7EB" }}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-display text-lg" style={{ color: "#0F172A" }}>Report profile</h3>
            <p className="text-[12.5px]" style={{ color: "#64748B" }}>
              @{handle} — reports are reviewed by SocialPulse moderators.
            </p>
          </div>
          <button onClick={onClose}
                  className="w-8 h-8 rounded-full inline-flex items-center justify-center hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        {done ? (
          <div data-testid="report-success" className="py-6 text-center">
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-full mb-3"
                 style={{ background: "rgba(16,185,129,0.10)", color: "#10B981" }}>
              <Flag className="w-5 h-5" />
            </div>
            <div className="font-display text-lg mb-1" style={{ color: "#0F172A" }}>Report received</div>
            <div className="text-[12.5px] mb-3" style={{ color: "#64748B" }}>
              Case ID: <code className="font-mono">{done}</code>
            </div>
            <button onClick={onClose}
                    className="px-4 py-2 rounded-full text-[13px] font-semibold text-white"
                    style={{ background: "#7C3AED" }}>Close</button>
          </div>
        ) : (
          <>
            <label className="block text-[12px] font-mono uppercase tracking-[0.15em] mb-1 opacity-70">
              What is the problem?
            </label>
            <div data-testid="report-reasons" className="grid grid-cols-1 gap-1 mb-3 max-h-[240px] overflow-y-auto">
              {REASONS.map((r) => (
                <label key={r.key}
                       className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 cursor-pointer">
                  <input type="radio" name="reason" value={r.key}
                         checked={reason === r.key}
                         onChange={(e) => setReason(e.target.value)} />
                  <span className="text-[13px]" style={{ color: "#334155" }}>{r.label}</span>
                </label>
              ))}
            </div>

            <textarea
              data-testid="report-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add details (optional)"
              rows={3}
              className="w-full rounded-md border p-2 text-[13px] mb-2 outline-none"
              style={{ borderColor: "#CBD5E1" }}
            />
            <label className="flex items-center gap-2 mb-3 text-[13px]" style={{ color: "#334155" }}>
              <input type="checkbox"
                     checked={autoBlock}
                     onChange={(e) => setAutoBlock(e.target.checked)} />
              Also block @{handle}
            </label>

            <div className="flex justify-end gap-2">
              <button onClick={onClose}
                      className="px-4 py-2 rounded-full text-[13px] font-medium border bg-white"
                      style={{ borderColor: "#CBD5E1", color: "#334155" }}>
                Cancel
              </button>
              <button
                data-testid="report-submit"
                onClick={submit}
                disabled={!reason || busy}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold text-white disabled:opacity-50"
                style={{ background: "#DC2626" }}>
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Submit report
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
